import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { generatePath, useNavigate, useParams } from 'react-router'
import { AppRoutes } from '../../Routes'
import { useEffect, useState } from 'react'
import {
  GetAnnualPlanAndOrgName,
  GetAnnualPlanStatus,
  GetTasksForAnnualPlan
} from '../../../services/AnnualPlanService'
import { TabDisplay } from '@oaisd/michdev.components.react'
import { ImplementationGetStarted } from './ImplementationGetStarted'
import { type AnnualPlanPhaseName, AnnualPlanPhaseSwitcher } from './AnnualPlanPhaseSwitcher'
import LockIcon from '@mui/icons-material/Lock'
import { RCNOperationsOverview } from '../planning/priorityActivities/rcnOperations/RCNOperationsOverview'
import { AnnualPlanStatus, ImplementationPhaseStatuses } from '../../../models/AnnualPlanStatus'
import { toast } from 'react-toastify'
import { useAnnualPlanUserRole } from '../../../hooks/use-annualPlanUserRole'
import { NotAllowed } from '../../masterComponents/ProtectedRoute'
import { PriorityActivitiesTab } from '../planning/priorityActivities/PriorityActivitiesTab'
import {
  PlanOrganizationTaskName,
  PlanOrganizationTaskStatus
} from '../../../models/PlanOrganizationTask'
import { AnnualPlanDashboard } from '../planning/AnnualPlanDashboard'
import { BudgetOverviewTab } from '../planning/budget/BudgetOverviewTab'
import { ImplementationDataTools } from './ImplementationDataTools'
import { useSearchParams } from 'react-router-dom'
import { SubmitPlanTab } from '../planning/SubmitPlanTab'
import { AnnualPlanRole } from '../../../models/AnnualPlanRole'
import { ProgressReportsTab } from './ProgressReports/ProgressReportsRouter'

export enum ImplementationDetailsTabs {
  GettingStarted = 0,
  Dashboard,
  RcnOperations,
  PriorityActivities,
  BudgetOverview,
  SubmitPlanUpdates,
  DataTools,
  ProgressReports
}

const LockedLabel = ({ tabName }: { tabName: string }): JSX.Element => {
  return <Box sx={{ display: 'flex' }}>
    <>{tabName}<LockIcon sx={{ marginLeft: '.5em', fontSize: '.875rem' }}/></>
  </Box>
}

export const ImplementationDetails = (): JSX.Element => {
  const nav = useNavigate()
  const { rcnId, planId, tab: tabString, planOrgId } = useParams()
  const [organizationName, setOrganizationName] = useState('')
  const [selectedTab, setSelectedTab] = useState(ImplementationDetailsTabs.GettingStarted)
  const [annualPlanName, setAnnualPlanName] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [annualPlanStatus, setAnnualPlanStatus] = useState<AnnualPlanStatus>(AnnualPlanStatus.NotStarted)
  const { isLoading: userRoleLoading, error, userRole } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)
  const implementationPhaseNotStarted = annualPlanStatus === AnnualPlanStatus.Finalized
  const [anyIncompleteTasks, setAnyIncompleteTasks] = useState(true)
  const tab = Number.parseInt(tabString ?? '0')
  const [updatesPhaseIsComplete, setUpdatesPhaseIsComplete] = useState(false)
  const [searchParams] = useSearchParams()
  const showCommentsQuery = searchParams.get('comments')
  const [showComments, setShowComments] = useState<boolean | undefined>()
  const [budgetOverviewTaskReadyToSubmit, setBudgetOverviewTaskReadyToSubmit] = useState(false)

  const loadAnnualPlanAndOrganizationName = async (rcnId: string, planId: string): Promise<void> => {
    const { annualPlanName, organizationName } = await GetAnnualPlanAndOrgName(planId, rcnId)
    setAnnualPlanName(annualPlanName)
    setOrganizationName(organizationName)
  }

  const loadAnnualPlanStatus = async (planId: string): Promise<void> => {
    const status = await GetAnnualPlanStatus(Number(planId))
    if (status == null) toast.error('Failed to load annual plan status')
    else {
      setAnnualPlanStatus(status)
      if (
        status === AnnualPlanStatus.Finalized &&
        tab !== ImplementationDetailsTabs.GettingStarted &&
        rcnId != null
      ) {
        nav(generatePath(
          AppRoutes.IMPLEMENTATION_DETAILS,
          { rcnId, planId, tab: String(ImplementationDetailsTabs.GettingStarted) }
        ))
      }
    }
  }

  const loadTasks = async (): Promise<void> => {
    const tasks = await GetTasksForAnnualPlan(Number(planId))
    if (tasks == null || tasks.length === 0) {
      return
    }
    setBudgetOverviewTaskReadyToSubmit(
      tasks.find(task =>
        task.taskName === PlanOrganizationTaskName.BudgetOverview &&
        task.status === PlanOrganizationTaskStatus.ReadyToSubmit
      ) != null
    )

    const trackingStatuses = [
      PlanOrganizationTaskStatus.UpdatesSubmitted,
      PlanOrganizationTaskStatus.TrackingInProgress,
      PlanOrganizationTaskStatus.TrackingCompleted
    ]
    const ignoreTaskNames = [
      PlanOrganizationTaskName.EPLITrainerInitialReview,
      PlanOrganizationTaskName.EPLITrainerFinalReview,
      PlanOrganizationTaskName.SignContract,
      PlanOrganizationTaskName.BudgetOverview,
      PlanOrganizationTaskName.PeerToPeerDataCollectionSystem,
      PlanOrganizationTaskName.CoachingCapacityTool,
      PlanOrganizationTaskName.FocusStudentDataBOY,
      PlanOrganizationTaskName.FocusStudentDataEOY
    ]
    const updatesPhaseIsComplete = tasks
      .filter(task => !ignoreTaskNames.includes(task.taskName as PlanOrganizationTaskName))
      .every(task => trackingStatuses.includes(task.status))
    setUpdatesPhaseIsComplete(updatesPhaseIsComplete)

    // Check for incomplete required tasks
    const requiredTasksToCheckIncomplete = tasks.filter(task => [
      PlanOrganizationTaskName.RcnOperations,
      PlanOrganizationTaskName.PriorityOne,
      PlanOrganizationTaskName.PriorityTwo,
      PlanOrganizationTaskName.PriorityThree,
      PlanOrganizationTaskName.PriorityFour
    ].includes(task.taskName as PlanOrganizationTaskName))
    const incompleteTasks = requiredTasksToCheckIncomplete
      .filter(task => task.status !== PlanOrganizationTaskStatus.UpdatesSubmitted)
    setAnyIncompleteTasks(incompleteTasks.length > 0)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (rcnId == null || planId == null) return
      setIsLoading(true)

      await Promise.all([
        loadAnnualPlanAndOrganizationName(rcnId, planId),
        loadAnnualPlanStatus(planId),
        loadTasks()
      ])

      setIsLoading(false)
    }

    void fetchData()

    setSelectedTab(tab)
  }, [rcnId, planId, planOrgId])

  useEffect(() => {
    if (showCommentsQuery === 'true') {
      setShowComments(true)
    }
  }, [showCommentsQuery])

  const navigateToImplementationInstance = (): void => {
    if (rcnId == null) return

    const path = generatePath(
      AppRoutes.IMPLEMENTATION_INSTANCE,
      { rcnId }
    )
    nav(path)
  }

  const updateTab = (tab: ImplementationDetailsTabs): void => {
    setSelectedTab(tab)
    if (rcnId == null) return
    const route = generatePath(
      AppRoutes.IMPLEMENTATION_DETAILS,
      { rcnId, planId: String(planId), tab: String(tab) }
    )
    nav(route)

    // load tasks when switching to Submit Plan Updates tab
    if (tab === ImplementationDetailsTabs.SubmitPlanUpdates) void loadTasks()
  }

  const handlePhaseChange = (phase: AnnualPlanPhaseName): void => {
    if (phase === 'implementation' || rcnId == null || planId == null) return

    const path = generatePath(
      AppRoutes.PLANNING_DETAILS,
      { rcnId, planId, tab: String(selectedTab) }
    )
    nav(path)
  }

  const handlePlanSubmitted = (): void => {
    if (planId != null) {
      void loadAnnualPlanStatus(planId)
      void loadTasks()
    }
    updateTab(ImplementationDetailsTabs.BudgetOverview)
  }

  const onImplementationStart = (): void => {
    updateTab(ImplementationDetailsTabs.Dashboard)
    setAnnualPlanStatus(AnnualPlanStatus.Updating)
  }

  const canViewDataTools = !implementationPhaseNotStarted && updatesPhaseIsComplete
  const canViewSubmitPlanUpdates = (
    !implementationPhaseNotStarted &&
    !anyIncompleteTasks &&
    budgetOverviewTaskReadyToSubmit &&
    userRole === AnnualPlanRole.PlanAdministrator
  )

  const tabs = [
    { label: 'Getting Started', value: ImplementationDetailsTabs.GettingStarted, dataTestId: 'gettingStartedTab' },
    { label: 'Dashboard', value: ImplementationDetailsTabs.Dashboard, dataTestId: 'dashboardTab', disabled: implementationPhaseNotStarted },
    { label: 'RCN Operations', value: ImplementationDetailsTabs.RcnOperations, dataTestId: 'rcnOperationsTab', disabled: implementationPhaseNotStarted },
    { label: 'Priority Activities', value: ImplementationDetailsTabs.PriorityActivities, dataTestId: 'priorityActivitiesTab', disabled: implementationPhaseNotStarted },
    { label: 'Budget Overview', value: ImplementationDetailsTabs.BudgetOverview, dataTestId: 'budgetOverviewTab', disabled: implementationPhaseNotStarted },
    { label: 'Submit Plan Updates', value: ImplementationDetailsTabs.SubmitPlanUpdates, dataTestId: 'submitPlanUpdatesTab', disabled: !canViewSubmitPlanUpdates },
    { label: 'Data Tools', value: ImplementationDetailsTabs.DataTools, dataTestId: 'dataToolsTab', disabled: !canViewDataTools },
    { label: 'Progress Reports', value: ImplementationDetailsTabs.ProgressReports, dataTestId: 'progressReportsTab', disabled: implementationPhaseNotStarted }
  ]

  if (!userRoleLoading && error != null) {
    return <Typography variant='body1' sx={{ textAlign: 'center' }}>{error}</Typography>
  }

  if (isLoading) return <CircularProgress data-testid='loadingSpinner'/>

  if (!ImplementationPhaseStatuses.includes(annualPlanStatus)) {
    return <NotAllowed />
  }

  return <>
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: '.5em' }}>
      <Link
        data-testid='backToPlanning'
        color="inherit"
        underline="hover"
        sx={{ cursor: 'pointer' }}
        onClick={navigateToImplementationInstance}
      >
        <ArrowBackIcon sx={{ verticalAlign: 'middle', mr: '5px' }} />
        Plan By Year
      </Link>
      <Typography color='inherit'>{organizationName}</Typography>
    </Breadcrumbs>

    <Stack direction='row' justifyContent='space-between'>
      <Stack spacing='.5em'>
        <Typography variant='h1' data-testid='pageHeader'>{annualPlanName}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h4' data-testid='subPageHeader'>{organizationName}</Typography>
        </Box>
      </Stack>

      <AnnualPlanPhaseSwitcher phase='implementation' onPhaseChange={handlePhaseChange} />
    </Stack>

    <Tabs
      sx={{ mb: '2em', mt: '1em' }}
      value={selectedTab}
      onChange={(_, tab) => { updateTab(tab) }}
    >
      {tabs.map(tab => (
        <Tab
          key={tab.label}
          data-testid={tab.dataTestId}
          label={tab.disabled === true ? <LockedLabel tabName={tab.label} /> : tab.label}
          value={tab.value}
          disabled={tab.disabled}
          sx={{ textTransform: 'none' }}
          onClick={() => { if (selectedTab === tab.value) updateTab(tab.value) }}
        />
      ))}
    </Tabs>

    <TabDisplay index={ImplementationDetailsTabs.GettingStarted} value={selectedTab}>
      <ImplementationGetStarted
        annualPlanId={Number(planId)}
        rcnId={Number(rcnId)}
        userRole={userRole}
        annualPlanStatus={annualPlanStatus}
        onImplementationStarted={onImplementationStart}
      />
    </TabDisplay>
    <TabDisplay index={ImplementationDetailsTabs.Dashboard} value={selectedTab}>
      <Box data-testid='implementationDashboard'>
        { !implementationPhaseNotStarted &&
          <AnnualPlanDashboard
            annualPlanId={Number(planId)}
            rcnId={Number(rcnId)}
            changeTab={updateTab}
            implementationPhase={true}
          />
        }
      </Box>
    </TabDisplay>
    <TabDisplay index={ImplementationDetailsTabs.RcnOperations} value={selectedTab}>
      {!implementationPhaseNotStarted && (
        <RCNOperationsOverview
          annualPlanId={Number(planId ?? 0)}
          rcnId={Number(rcnId)}
          showComments={false}
          onUpdateStatus={() => {
          }}
        />
      )}
    </TabDisplay>
    <TabDisplay index={ImplementationDetailsTabs.PriorityActivities} value={selectedTab}>
      {!implementationPhaseNotStarted && (
        <PriorityActivitiesTab
          annualPlanId={Number(planId)}
          rcnId={Number(rcnId)}
          onUpdateStatus={loadTasks}
          implementationPhase={true}
        />
      )}
    </TabDisplay>
    <TabDisplay index={ImplementationDetailsTabs.BudgetOverview} value={selectedTab}>
      {!implementationPhaseNotStarted &&
        <BudgetOverviewTab
          annualPlanId={Number(planId)}
          rcnId={Number(rcnId)}
          organizationName={organizationName}
          showComments={showComments}
          changeTab={updateTab}
          implementationPhase={true}
          onUpdateStatus={async () => { await loadAnnualPlanStatus(planId ?? '') }}
        />
      }
    </TabDisplay>
    <TabDisplay index={ImplementationDetailsTabs.SubmitPlanUpdates} value={selectedTab}>
      {canViewSubmitPlanUpdates && (
        <SubmitPlanTab
          annualPlanId={Number(planId)}
          rcnId={Number(rcnId)}
          onSubmit={handlePlanSubmitted}
          isImplementation={true}
        />
      )}
    </TabDisplay>
    <TabDisplay index={ImplementationDetailsTabs.DataTools} value={selectedTab}>
      {canViewDataTools && <ImplementationDataTools annualPlanId={Number(planId)} rcnId={Number(rcnId)} />}
    </TabDisplay>
    <TabDisplay index={ImplementationDetailsTabs.ProgressReports} value={selectedTab} >
      {!implementationPhaseNotStarted && (
        <ProgressReportsTab />
      )}
    </TabDisplay>
  </>
}
